import { assert } from '@core/utils';

interface InfiniteQueryResponse {
  context?: {
    totalCount: number;
    size: number;
    page: number;
  };
}

/**
 * TanStack Query의 무한 스크롤 쿼리 옵션을 생성하는 유틸리티 함수
 *
 * API 응답의 context 정보(total_count, size, page)를 기반으로
 * 다음 페이지 존재 여부를 자동으로 판단하고 페이지 파라미터를 관리합니다.
 *
 * @template T - 기본 쿼리 옵션 타입
 * @template R - API 응답 타입 (InfiniteQueryResponse를 확장해야 함)
 *
 * @param options - TanStack Query의 기본 쿼리 옵션 객체
 *
 * @returns 무한 스크롤에 필요한 getNextPageParam과 initialPageParam이 추가된 쿼리 옵션
 *
 * @example
 * ```tsx
 * const queryOptions = filledInfiniteQueryOptions({
 *   queryKey: ['reviews', productId],
 *   queryFn: ({ pageParam }) => fetchReviews(productId, pageParam),
 * });
 *
 * const { data, fetchNextPage, hasNextPage } = useInfiniteQuery(queryOptions);
 * ```
 */
export const filledInfiniteQueryOptions = <
  T extends object,
  R extends InfiniteQueryResponse,
>(
  options: T,
) => ({
  ...options,
  getNextPageParam: (lastPage: R) => {
    assert(lastPage.context);

    const { totalCount: total_count, size, page } = lastPage.context;

    const hasNextPage = total_count > size * page;

    if (hasNextPage) return page + 1;
  },
  initialPageParam: 1,
});
